import jsPDF from 'jspdf';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';


/**
 * Exports career plan as PDF using jsPDF
 * @param {Object} plan - The career plan object
 * @param {string} fileName - The filename for export
 */
export const exportToPDF = async (plan, fileName = 'career-plan') => {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Set up document metadata
    doc?.setProperties({
      title: `Career Development Plan - ${plan?.occupation || 'Professional'}`,
      subject: 'Career Development Plan',
      author: 'CareerLift AI',
      keywords: 'career, development, plan, professional, growth',
      creator: 'CareerLift Application'
    });

    // Add header
    doc?.setFontSize(20);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Career Development Plan', 20, 25);
    
    // Add subtitle
    doc?.setFontSize(14);
    doc?.setFont('helvetica', 'normal');
    doc?.text(`${plan?.occupation || 'Professional'} - ${plan?.seniority || 'Career'} Level`, 20, 35);
    
    let yPosition = 50;
    
    // Add plan overview
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Plan Overview', 20, yPosition);
    yPosition += 10;
    
    doc?.setFont('helvetica', 'normal');
    const overviewText = [
      `Estimated Timeline: ${plan?.estimatedWeeks || 'N/A'} weeks`,
      `Weekly Commitment: ${plan?.estimatedHoursPerWeek || 'N/A'} hours`,
      `Total Exercises: ${plan?.totalExercises || 'N/A'}`,
      `Created: ${plan?.createdAt ? new Date(plan.createdAt)?.toLocaleDateString() : 'N/A'}`
    ];
    
    overviewText?.forEach(text => {
      doc?.text(text, 25, yPosition);
      yPosition += 7;
    });
    
    yPosition += 10;
    
    // Add sections
    plan?.sections?.forEach((section, index) => {
      // Check if we need a new page
      if (yPosition > 250) {
        doc?.addPage();
        yPosition = 20;
      }
      
      // Section header
      doc?.setFontSize(14);
      doc?.setFont('helvetica', 'bold');
      const sectionTitle = section?.type?.charAt(0)?.toUpperCase() + section?.type?.slice(1)?.replace('_', ' ') || `Section ${index + 1}`;
      doc?.text(sectionTitle, 20, yPosition);
      yPosition += 10;
      
      // Section content
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      
      if (section?.content) {
        const contentLines = doc?.splitTextToSize(section?.content, 170);
        contentLines?.forEach(line => {
          if (yPosition > 270) {
            doc?.addPage();
            yPosition = 20;
          }
          doc?.text(line, 25, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }
      
      // Section items
      if (section?.items && Array.isArray(section?.items)) {
        section?.items?.forEach((item, itemIndex) => {
          if (yPosition > 260) {
            doc?.addPage();
            yPosition = 20;
          }
          
          // Item title
          doc?.setFont('helvetica', 'bold');
          doc?.text(`${itemIndex + 1}. ${item?.title || 'Item'}`, 30, yPosition);
          yPosition += 6;
          
          // Item description
          doc?.setFont('helvetica', 'normal');
          if (item?.description) {
            const descLines = doc?.splitTextToSize(item?.description, 160);
            descLines?.forEach(line => {
              if (yPosition > 270) {
                doc?.addPage();
                yPosition = 20;
              }
              doc?.text(line, 35, yPosition);
              yPosition += 4;
            });
          }
          
          // Item metadata
          if (item?.timeframe || item?.priority) {
            doc?.setFontSize(9);
            doc?.setTextColor(100);
            const metadata = [];
            if (item?.timeframe) metadata?.push(`Timeline: ${item?.timeframe}`);
            if (item?.priority) metadata?.push(`Priority: ${item?.priority}`);
            doc?.text(metadata?.join(' | '), 35, yPosition);
            yPosition += 4;
            doc?.setFontSize(10);
            doc?.setTextColor(0);
          }
          
          yPosition += 3;
        });
      }
      
      // Section metrics
      if (section?.metrics && Array.isArray(section?.metrics)) {
        yPosition += 5;
        doc?.setFont('helvetica', 'bold');
        doc?.text('Key Metrics:', 25, yPosition);
        yPosition += 7;
        
        section?.metrics?.forEach((metric, metricIndex) => {
          if (yPosition > 260) {
            doc?.addPage();
            yPosition = 20;
          }
          
          doc?.setFont('helvetica', 'normal');
          doc?.text(`• ${metric?.name || 'Metric'}: ${metric?.target || 'N/A'}`, 30, yPosition);
          yPosition += 6;
          
          if (metric?.description) {
            doc?.setFontSize(9);
            doc?.setTextColor(100);
            const metricDesc = doc?.splitTextToSize(metric?.description, 150);
            metricDesc?.forEach(line => {
              doc?.text(line, 35, yPosition);
              yPosition += 4;
            });
            doc?.setFontSize(10);
            doc?.setTextColor(0);
          }
          yPosition += 2;
        });
      }
      
      yPosition += 15;
    });
    
    // Add footer with generation info
    const pageCount = doc?.internal?.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc?.setPage(i);
      doc?.setFontSize(8);
      doc?.setTextColor(100);
      doc?.text(`Generated by CareerLift AI - ${new Date()?.toLocaleDateString()}`, 20, 287);
      doc?.text(`Page ${i} of ${pageCount}`, 170, 287);
    }
    
    // Save the PDF
    doc?.save(`${fileName}.pdf`);
    return true;
    
  } catch (error) {
    console.error('PDF export failed:', error);
    throw new Error('Failed to export PDF. Please try again.');
  }
};

/**
 * Exports career plan as CSV using PapaParse
 * @param {Object} plan - The career plan object
 * @param {string} fileName - The filename for export
 */
export const exportToCSV = async (plan, fileName = 'career-plan') => {
  try {
    // Prepare CSV data structure
    const csvData = [];
    
    // Add header row
    csvData?.push([
      'Section',
      'Type',
      'Title',
      'Description', 
      'Timeframe',
      'Priority',
      'Target/Goal',
      'Category'
    ]);
    
    // Add plan overview row
    csvData?.push([
      'Overview',
      'plan_info',
      `${plan?.occupation || 'Professional'} Development Plan`,
      `Career development plan for ${plan?.seniority || 'Professional'} level`,
      `${plan?.estimatedWeeks || 'N/A'} weeks`,
      'High',
      `${plan?.estimatedHoursPerWeek || 'N/A'} hours/week`,
      'Plan Metadata'
    ]);
    
    // Process each section
    plan?.sections?.forEach((section) => {
      const sectionName = section?.type?.charAt(0)?.toUpperCase() + section?.type?.slice(1)?.replace('_', ' ') || 'Section';
      
      // Add section header
      if (section?.content) {
        csvData?.push([
          sectionName,
          'section_header',
          sectionName,
          section?.content,
          '',
          '',
          '',
          'Section Description'
        ]);
      }
      
      // Add section items
      if (section?.items && Array.isArray(section?.items)) {
        section?.items?.forEach((item, index) => {
          csvData?.push([
            sectionName,
            'action_item',
            item?.title || `${sectionName} Item ${index + 1}`,
            item?.description || '',
            item?.timeframe || '',
            item?.priority || '',
            '',
            'Action Item'
          ]);
        });
      }
      
      // Add section metrics
      if (section?.metrics && Array.isArray(section?.metrics)) {
        section?.metrics?.forEach((metric, index) => {
          csvData?.push([
            sectionName,
            'metric',
            metric?.name || `Metric ${index + 1}`,
            metric?.description || '',
            '',
            '',
            metric?.target || '',
            'KPI/Metric'
          ]);
        });
      }
    });
    
    // Convert to CSV string
    const csvString = Papa?.unparse(csvData, {
      header: false,
      delimiter: ',',
      quotes: true,
      quoteChar: '"',
      escapeChar: '"',
      newline: '\r\n'
    });
    
    // Create and save file
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${fileName}.csv`);
    return true;
    
  } catch (error) {
    console.error('CSV export failed:', error);
    throw new Error('Failed to export CSV. Please try again.');
  }
};

/**
 * Exports career plan as structured JSON
 * @param {Object} plan - The career plan object  
 * @param {string} fileName - The filename for export
 */
export const exportToJSON = async (plan, fileName = 'career-plan') => {
  try {
    // Create a clean export version of the plan
    const exportData = {
      metadata: {
        exportDate: new Date()?.toISOString(),
        exportVersion: '1.0',
        source: 'CareerLift AI Platform'
      },
      plan: {
        id: plan?.id || 'unknown',
        occupation: plan?.occupation || '',
        seniority: plan?.seniority || '',
        estimatedWeeks: plan?.estimatedWeeks || 0,
        estimatedHoursPerWeek: plan?.estimatedHoursPerWeek || '',
        totalExercises: plan?.totalExercises || 0,
        createdAt: plan?.createdAt || new Date(),
        lastUpdated: plan?.lastUpdated || new Date(),
        sections: plan?.sections || []
      }
    };
    
    // Convert to JSON string with formatting
    const jsonString = JSON.stringify(exportData, null, 2);
    
    // Create and save file
    const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8;' });
    saveAs(blob, `${fileName}.json`);
    return true;
    
  } catch (error) {
    console.error('JSON export failed:', error);
    throw new Error('Failed to export JSON. Please try again.');
  }
};

/**
 * Validates export data before processing
 * @param {Object} plan - The career plan object
 * @returns {boolean} - Whether the plan data is valid for export
 */
export const validatePlanForExport = (plan) => {
  if (!plan) {
    throw new Error('No plan data available for export');
  }
  
  if (!plan?.sections || !Array.isArray(plan?.sections) || plan?.sections?.length === 0) {
    throw new Error('Plan must contain at least one section to export');
  }
  
  return true;
};

/**
 * Gets appropriate filename based on plan data
 * @param {Object} plan - The career plan object
 * @returns {string} - Generated filename
 */
export const generateFileName = (plan) => {
  const occupation = plan?.occupation?.toLowerCase()?.replace(/[^a-z0-9]/g, '-') || 'career';
  const seniority = plan?.seniority?.toLowerCase()?.replace(/[^a-z0-9]/g, '-') || 'plan';
  const timestamp = new Date()?.toISOString()?.split('T')?.[0];
  
  return `${occupation}-${seniority}-plan-${timestamp}`;
};

/**
 * Main export function that handles all formats
 * @param {Object} plan - The career plan object
 * @param {string} format - Export format ('pdf', 'csv', 'json')
 * @param {string} customFileName - Optional custom filename
 */
export const exportPlan = async (plan, format, customFileName = null) => {
  try {
    // Validate plan data
    validatePlanForExport(plan);
    
    // Generate filename
    const fileName = customFileName || generateFileName(plan);
    
    // Export based on format
    switch (format?.toLowerCase()) {
      case 'pdf':
        return await exportToPDF(plan, fileName);
      case 'csv':
        return await exportToCSV(plan, fileName);
      case 'json':
        return await exportToJSON(plan, fileName);
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
    
  } catch (error) {
    console.error(`Export failed for format ${format}:`, error);
    throw error;
  }
};